import {
  ConfirmDialog,
  EmptyState,
  PageError,
  PageHeader,
  PageLoader,
  PermissionButton,
} from "@/components/common";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { useDebounce } from "@/hooks";
import { notify } from "@/lib/notify";
import type { Product } from "@/types";
import { Coffee, Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useAttributes,
  useCategories,
  useDeleteProduct,
  useProducts,
  useToggleProduct,
} from "../api";
import { ProductFormDialog } from "../components";
import { ProductsCardList } from "../components/ProductsCardList";
import { ProductsTable } from "../components/ProductsTable";

type StatusFilter = "all" | "active" | "inactive";

export function ProductsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | "all">(
    "all",
  );
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<Product | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  const {
    data: productsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useProducts({
    all: true,
    status: statusFilter,
    categoryId: selectedCategoryId !== "all" ? selectedCategoryId : undefined,
    search: debouncedSearch || undefined,
    page,
    limit: pageSize,
  });
  const { data: categories } = useCategories({ all: true });
  const { data: attributes } = useAttributes();
  const toggleProduct = useToggleProduct();
  const deleteProduct = useDeleteProduct();

  const products = productsData?.data ?? [];
  const pagination = productsData?.pagination;

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const openEditor = (product: Product) => {
    navigate(`/products/${product.id}?tab=details`);
  };

  const handleToggle = async (id: string) => {
    try {
      await toggleProduct.mutateAsync(id);
      notify.success("Product status updated");
    } catch (err) {
      notify.fromError(err, "Failed to update product");
    }
  };

  const handleDelete = async () => {
    if (!deleteCandidate) {
      return;
    }

    try {
      await deleteProduct.mutateAsync(deleteCandidate.id);
      notify.success("Product deleted");
      setDeleteCandidate(null);
    } catch (err) {
      notify.fromError(err, "Failed to delete product");
    }
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategoryId(value);
    setPage(1);
  };

  const handleStatusChange = (value: StatusFilter) => {
    setStatusFilter(value);
    setPage(1);
  };

  if (isLoading && !products.length) {
    return <PageLoader />;
  }

  if (error) {
    return <PageError message="Failed to load products" onRetry={refetch} />;
  }

  return (
    <div className="container mx-auto p-4 sm:p-6">
      <PageHeader
        title="Products"
        description="Manage your product catalog"
        actions={
          <PermissionButton
            permission="products.create"
            onClick={() => setIsCreateOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Product
          </PermissionButton>
        }
      />
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Coffee className="h-5 w-5" />
                Products ({pagination?.total || 0})
              </CardTitle>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
              <div className="flex gap-2">
                <Select
                  value={selectedCategoryId}
                  onValueChange={handleCategoryChange}
                >
                  <SelectTrigger className="flex-1 sm:w-40">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories?.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={handleStatusChange}>
                  <SelectTrigger className="flex-1 sm:w-32">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent
          className={
            isFetching
              ? "opacity-60 transition-opacity duration-200"
              : "transition-opacity duration-200"
          }
        >
          {!products.length ? (
            <EmptyState
              icon={Coffee}
              title="No products found"
              description={
                search
                  ? "Try adjusting your search"
                  : "Get started by adding a new product"
              }
              action={
                !search && (
                  <PermissionButton
                    permission="products.create"
                    onClick={() => setIsCreateOpen(true)}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Product
                  </PermissionButton>
                )
              }
            />
          ) : (
            <>
              <ProductsCardList
                products={products}
                onEdit={openEditor}
                onDelete={setDeleteCandidate}
                onToggle={handleToggle}
                onView={(id) => navigate(`/products/${id}`)}
              />
              <ProductsTable
                products={products}
                onEdit={openEditor}
                onDelete={setDeleteCandidate}
                onToggle={handleToggle}
                onView={(id) => navigate(`/products/${id}`)}
              />
              <Pagination
                page={page}
                pageSize={pageSize}
                total={pagination?.total ?? 0}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
              />
            </>
          )}
        </CardContent>
      </Card>
      <ProductFormDialog
        open={isCreateOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateOpen(false);
          }
        }}
        categories={categories || []}
        attributes={attributes || []}
      />
      <ConfirmDialog
        open={!!deleteCandidate}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteCandidate(null);
          }
        }}
        title="Delete product?"
        description={
          <>
            This will remove{" "}
            <span className="font-medium text-foreground">
              {deleteCandidate?.name}
            </span>{" "}
            from your catalog. Past orders that include it will still show its
            name on the receipt.
          </>
        }
        confirmLabel="Delete"
        loadingLabel="Deleting..."
        destructive
        loading={deleteProduct.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
